import React, { useRef, useState } from "react";
import styled from "styled-components";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import DataTable from "./DataTable";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { saveAs } from "file-saver";

const VisualizerContainer = styled.div`
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  gap: 10px;
`;

const FilterButton = styled.button`
  background-color: ${(props) => (props.selected ? "#6ed216" : "#f5f5f5")};
  color: ${(props) => (props.selected ? "white" : "#333")};
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background-color: ${(props) => (props.selected ? "#5cb70f" : "#e0e0e0")};
  }
`;

const ChartTitle = styled.h3`
  text-align: center;
  margin-bottom: 20px;
  color: #333;
`;

const ChartContainer = styled.div`
  height: 400px;
`;

// Nouveau style pour le conteneur des boutons d'export et de changement de vue
const ActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
`;

// Style pour les boutons d'export
const ExportContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ExportButton = styled.button`
  background-color: #6ed216;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    background-color: #5cb70f;
  }
`;

// Nouveau style pour les boutons de changement de type de graphique
const ChartTypeContainer = styled.div`
  display: flex;
  gap: 5px;
`;

const ChartTypeButton = styled.button`
  background-color: ${(props) => (props.selected ? "#6ed216" : "#f5f5f5")};
  color: ${(props) => (props.selected ? "white" : "#333")};
  border: none;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    background-color: ${(props) => (props.selected ? "#5cb70f" : "#e0e0e0")};
  }
`;

// Composant personnalisé pour le tooltip avec formatage spécifique
const CustomTooltip = ({ active, payload, label, selectedKPI }) => {
  if (active && payload && payload.length) {
    // Fonction pour formater les valeurs selon le type de KPI
    const formatValue = (value) => {
      if (selectedKPI === "budget") {
        // Format pour les prix en euros: arrondi au dixième avec virgule
        return value.toLocaleString("fr-FR", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        });
      } else if (selectedKPI === "ctr" || selectedKPI === "viewability") {
        // Format pour les pourcentages
        return (
          value.toLocaleString("fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) + "%"
        );
      } else {
        // Format pour les nombres entiers
        return value.toLocaleString("fr-FR");
      }
    };

    return (
      <div
        style={{
          backgroundColor: "white",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "4px",
        }}
      >
        <p style={{ margin: 0 }}>{`${label}`}</p>
        <p style={{ margin: 0, fontWeight: "bold" }}>
          {`${formatValue(payload[0].value)}`}
        </p>
      </div>
    );
  }

  return null;
};

// Couleurs pour les graphiques en camembert et autres
const COLORS = ['#6ed216', '#4285f4', '#fbbc05', '#ea4335', '#34a853', '#ff6d01', '#46bdc6', '#7baaf7'];

function DataVisualizer({ data, selectedKPI, selectedFilter, onFilterChange }) {
  const chartRef = useRef(null);
  // Nouvel état pour le type de graphique
  const [chartType, setChartType] = useState("bar");
  
  // Fonction pour formater les données selon le filtre sélectionné
  const getFilteredData = () => {
    if (selectedFilter === "day") {
      // Regrouper par jour
      const dailyData = {};
      data.forEach((item) => {
        if (!dailyData[item.date]) {
          dailyData[item.date] = {
            date: item.date,
            impressions: 0,
            clicks: 0,
            budget: 0,
            viewableImpressions: 0,
            measurableImpressions: 0,
            totalImpressions: 0,
            // Ajouter un accumulateur pour la viewability pondérée
            weightedViewability: 0,
            viewabilityCount: 0,
          };
        }

        // Accumuler les valeurs de base
        dailyData[item.date].impressions += Number(item.impressions || 0);
        dailyData[item.date].clicks += Number(item.clicks || 0);
        dailyData[item.date].budget += Number(item.budget || 0);
        dailyData[item.date].viewableImpressions += Number(
          item.viewableImpressions || 0
        );
        dailyData[item.date].measurableImpressions += Number(
          item.measurableImpressions || 0
        );
        dailyData[item.date].totalImpressions += Number(item.impressions || 0);

        // Si l'item a une valeur de viewability, l'ajouter à la moyenne pondérée
        if (item.viewability > 0) {
          dailyData[item.date].weightedViewability +=
            Number(item.viewability) * Number(item.measurableImpressions || 0);
          dailyData[item.date].viewabilityCount += Number(
            item.measurableImpressions || 0
          );
        }
      });

      // Calculer les métriques dérivées comme des moyennes
      Object.values(dailyData).forEach((day) => {
        // CTR est calculé comme (total des clics / total des impressions)
        day.ctr =
          day.totalImpressions > 0
            ? (day.clicks / day.totalImpressions) * 100
            : 0;

        // Viewability: préférer la moyenne pondérée si disponible, sinon calculer
        if (day.viewabilityCount > 0) {
          day.viewability = day.weightedViewability / day.viewabilityCount;
        } else if (day.measurableImpressions > 0) {
          day.viewability =
            (day.viewableImpressions / day.measurableImpressions) * 100;
        } else {
          day.viewability = 0;
        }

        // Sélectionner la métrique demandée pour l'affichage
        if (selectedKPI === "ctr") {
          day[selectedKPI] = day.ctr;
        } else if (selectedKPI === "viewability") {
          day[selectedKPI] = day.viewability;
        }
      });

      return Object.values(dailyData).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
    } else if (selectedFilter === "creative") {
      // Regrouper par bannière
      const creativeData = {};
      data.forEach((item) => {
        const creativeKey =
          item.creative || `Creative ${item.creativeId || "Unknown"}`;

        if (!creativeData[creativeKey]) {
          creativeData[creativeKey] = {
            name: creativeKey,
            impressions: 0,
            clicks: 0,
            budget: 0,
            viewableImpressions: 0,
            measurableImpressions: 0,
            totalImpressions: 0,
            creativeId: item.creativeId,
            // Ajouter un accumulateur pour la viewability pondérée
            weightedViewability: 0,
            viewabilityCount: 0,
          };
        }

        // Accumuler les valeurs de base
        creativeData[creativeKey].impressions += Number(item.impressions || 0);
        creativeData[creativeKey].clicks += Number(item.clicks || 0);
        creativeData[creativeKey].budget += Number(item.budget || 0);
        creativeData[creativeKey].viewableImpressions += Number(
          item.viewableImpressions || 0
        );
        creativeData[creativeKey].measurableImpressions += Number(
          item.measurableImpressions || 0
        );
        creativeData[creativeKey].totalImpressions += Number(
          item.impressions || 0
        );

        // Si l'item a une valeur de viewability, l'ajouter à la moyenne pondérée
        if (item.viewability > 0) {
          creativeData[creativeKey].weightedViewability +=
            Number(item.viewability) * Number(item.measurableImpressions || 0);
          creativeData[creativeKey].viewabilityCount += Number(
            item.measurableImpressions || 0
          );
        }
      });

      // Calculer les métriques dérivées comme des moyennes
      Object.values(creativeData).forEach((creative) => {
        // CTR est calculé comme (total des clics / total des impressions)
        creative.ctr =
          creative.totalImpressions > 0
            ? (creative.clicks / creative.totalImpressions) * 100
            : 0;

        // Viewability: préférer la moyenne pondérée si disponible, sinon calculer
        if (creative.viewabilityCount > 0) {
          creative.viewability =
            creative.weightedViewability / creative.viewabilityCount;
        } else if (creative.measurableImpressions > 0) {
          creative.viewability =
            (creative.viewableImpressions / creative.measurableImpressions) *
            100;
        } else {
          creative.viewability = 0;
        }

        // Sélectionner la métrique demandée pour l'affichage
        if (selectedKPI === "ctr") {
          creative[selectedKPI] = creative.ctr;
        } else if (selectedKPI === "viewability") {
          creative[selectedKPI] = creative.viewability;
        }
      });

      return Object.values(creativeData);
    } else if (selectedFilter === "size") {
      // Regrouper par taille
      const sizeData = {};
      data.forEach((item) => {
        if (!item.size) return;

        if (!sizeData[item.size]) {
          sizeData[item.size] = {
            size: item.size,
            impressions: 0,
            clicks: 0,
            budget: 0,
            viewableImpressions: 0,
            measurableImpressions: 0,
            totalImpressions: 0,
            // Ajouter un accumulateur pour la viewability pondérée
            weightedViewability: 0,
            viewabilityCount: 0,
          };
        }

        // Accumuler les valeurs de base
        sizeData[item.size].impressions += Number(item.impressions || 0);
        sizeData[item.size].clicks += Number(item.clicks || 0);
        sizeData[item.size].budget += Number(item.budget || 0);
        sizeData[item.size].viewableImpressions += Number(
          item.viewableImpressions || 0
        );
        sizeData[item.size].measurableImpressions += Number(
          item.measurableImpressions || 0
        );
        sizeData[item.size].totalImpressions += Number(item.impressions || 0);

        // Si l'item a une valeur de viewability, l'ajouter à la moyenne pondérée
        if (item.viewability > 0) {
          sizeData[item.size].weightedViewability +=
            Number(item.viewability) * Number(item.measurableImpressions || 0);
          sizeData[item.size].viewabilityCount += Number(
            item.measurableImpressions || 0
          );
        }
      });

      // Calculer les métriques dérivées comme des moyennes
      Object.values(sizeData).forEach((size) => {
        // CTR est calculé comme (total des clics / total des impressions)
        size.ctr =
          size.totalImpressions > 0
            ? (size.clicks / size.totalImpressions) * 100
            : 0;

        // Viewability: préférer la moyenne pondérée si disponible, sinon calculer
        if (size.viewabilityCount > 0) {
          size.viewability = size.weightedViewability / size.viewabilityCount;
        } else if (size.measurableImpressions > 0) {
          size.viewability =
            (size.viewableImpressions / size.measurableImpressions) * 100;
        } else {
          size.viewability = 0;
        }

        // Sélectionner la métrique demandée pour l'affichage
        if (selectedKPI === "ctr") {
          size[selectedKPI] = size.ctr;
        } else if (selectedKPI === "viewability") {
          size[selectedKPI] = size.viewability;
        }
      });

      return Object.values(sizeData);
    }
    return [];
  };
  const chartData = getFilteredData();

  // Déterminer le nom de la clé d'identification en fonction du filtre
  const getIdKey = () => {
    if (selectedFilter === "day") return "date";
    if (selectedFilter === "creative") return "name";
    return "size";
  };

  const idKey = getIdKey();

  // Fonction pour formater les données pour le graphique en camembert
  const getPieData = () => {
    // Pour le graphique en camembert, nous voulons limiter le nombre d'éléments
    // pour éviter que le graphique ne devienne illisible
    const maxSlices = 8;
    let pieData = [...chartData];
    
    // Trier les données par valeur décroissante
    pieData.sort((a, b) => b[selectedKPI] - a[selectedKPI]);
    
    // Si nous avons plus d'éléments que maxSlices, regrouper les plus petits
    if (pieData.length > maxSlices) {
      const topItems = pieData.slice(0, maxSlices - 1);
      const otherItems = pieData.slice(maxSlices - 1);
      
      // Calculer la somme des autres éléments
      const otherSum = otherItems.reduce((sum, item) => sum + item[selectedKPI], 0);
      
      // Ajouter un élément "Autres" avec la somme
      topItems.push({
        [idKey]: "Autres",
        [selectedKPI]: otherSum
      });
      
      return topItems;
    }
    
    return pieData;
  };

  // Fonction pour obtenir le titre du graphique en fonction du KPI et du filtre sélectionnés
  const getChartTitle = () => {
    const kpiLabels = {
      impressions: "Impressions",
      clicks: "Clics",
      ctr: "Taux de clics (CTR)",
      budget: "Budget",
      viewability: "Visibilité",
    };

    const filterLabels = {
      day: "par jour",
      creative: "par bannière",
      size: "par taille",
    };

    return `${kpiLabels[selectedKPI] || selectedKPI} ${
      filterLabels[selectedFilter] || ""
    }`;
  };

  // Fonction pour obtenir le format de l'axe Y en fonction du KPI sélectionné
  const getYAxisTickFormatter = () => {
    if (selectedKPI === "budget") {
      return (value) =>
        value.toLocaleString("fr-FR", {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        });
    } else if (selectedKPI === "ctr" || selectedKPI === "viewability") {
      return (value) =>
        value.toLocaleString("fr-FR", {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }) + "%";
    } else {
      return (value) => value.toLocaleString("fr-FR");
    }
  };

  // Fonction pour obtenir le format de l'axe X en fonction du filtre sélectionné
  const getXAxisTickFormatter = () => {
    if (selectedFilter === "day") {
      return (value) => {
        const date = new Date(value);
        return date.toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
        });
      };
    } else if (selectedFilter === "creative" || selectedFilter === "size") {
      return (value) => {
        // Tronquer les noms trop longs
        if (value && value.length > 15) {
          return value.substring(0, 12) + "...";
        }
        return value;
      };
    }
    return (value) => value;
  };

  // Fonction pour exporter le graphique en PNG
  const exportToPNG = () => {
    if (chartRef.current) {
      html2canvas(chartRef.current).then((canvas) => {
        canvas.toBlob((blob) => {
          saveAs(
            blob,
            `graphique_${selectedKPI}_${selectedFilter}_${new Date().toISOString().slice(
              0,
              10
            )}.png`
          );
        });
      });
    }
  };

  // Fonction pour exporter le graphique en PDF
  const exportToPDF = () => {
    if (chartRef.current) {
      html2canvas(chartRef.current).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("landscape", "mm", "a4");
        const imgWidth = 280;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
        pdf.save(
          `graphique_${selectedKPI}_${selectedFilter}_${new Date().toISOString().slice(
            0,
            10
          )}.pdf`
        );
      });
    }
  };

  // Fonction pour rendre le graphique approprié en fonction du type sélectionné
  const renderChart = () => {
    // Vérifier si nous avons des données
    if (!chartData || chartData.length === 0) {
      return (
        <div
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "#666",
          }}
        >
          Aucune donnée disponible pour ce filtre
        </div>
      );
    }

    // Graphique en barres
    if (chartType === "bar") {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey={idKey}
              tickFormatter={getXAxisTickFormatter()}
              angle={-45}
              textAnchor="end"
              height={70}
            />
            <YAxis tickFormatter={getYAxisTickFormatter()} />
            <Tooltip content={<CustomTooltip selectedKPI={selectedKPI} />} />
            <Legend />
            <Bar
              dataKey={selectedKPI}
              fill="#6ed216"
              name={getChartTitle().split(" ")[0]}
            />
          </BarChart>
        </ResponsiveContainer>
      );
    }
    
    // Graphique en ligne
    if (chartType === "line") {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey={idKey}
              tickFormatter={getXAxisTickFormatter()}
              angle={-45}
              textAnchor="end"
              height={70}
            />
            <YAxis tickFormatter={getYAxisTickFormatter()} />
            <Tooltip content={<CustomTooltip selectedKPI={selectedKPI} />} />
            <Legend />
            <Line
              type="monotone"
              dataKey={selectedKPI}
              stroke="#6ed216"
              activeDot={{ r: 8 }}
              name={getChartTitle().split(" ")[0]}
            />
          </LineChart>
        </ResponsiveContainer>
      );
    }
    
    // Graphique en aire
    if (chartType === "area") {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey={idKey}
              tickFormatter={getXAxisTickFormatter()}
              angle={-45}
              textAnchor="end"
              height={70}
            />
            <YAxis tickFormatter={getYAxisTickFormatter()} />
            <Tooltip content={<CustomTooltip selectedKPI={selectedKPI} />} />
            <Legend />
            <Area
              type="monotone"
              dataKey={selectedKPI}
              fill="#6ed216"
              stroke="#6ed216"
              name={getChartTitle().split(" ")[0]}
            />
          </AreaChart>
        </ResponsiveContainer>
      );
    }
    
    // Graphique en camembert
    if (chartType === "pie") {
      const pieData = getPieData();
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={true}
              outerRadius={150}
              fill="#8884d8"
              dataKey={selectedKPI}
              nameKey={idKey}
              label={({ name, percent }) => 
                `${name}: ${(percent * 100).toFixed(1)}%`
              }
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value) => {
                if (selectedKPI === "budget") {
                  return value.toLocaleString("fr-FR", {
                    style: "currency",
                    currency: "EUR",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                } else if (selectedKPI === "ctr" || selectedKPI === "viewability") {
                  return value.toLocaleString("fr-FR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }) + "%";
                } else {
                  return value.toLocaleString("fr-FR");
                }
              }}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    
    // Par défaut, retourner un graphique en barres
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={idKey}
            tickFormatter={getXAxisTickFormatter()}
            angle={-45}
            textAnchor="end"
            height={70}
          />
          <YAxis tickFormatter={getYAxisTickFormatter()} />
          <Tooltip content={<CustomTooltip selectedKPI={selectedKPI} />} />
          <Legend />
          <Bar
            dataKey={selectedKPI}
            fill="#6ed216"
            name={getChartTitle().split(" ")[0]}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <VisualizerContainer>
      <FilterContainer>
        <FilterButton
          selected={selectedFilter === "day"}
          onClick={() => onFilterChange("day")}
        >
          Par jour
        </FilterButton>
        <FilterButton
          selected={selectedFilter === "creative"}
          onClick={() => onFilterChange("creative")}
        >
          Par bannière
        </FilterButton>
        <FilterButton
          selected={selectedFilter === "size"}
          onClick={() => onFilterChange("size")}
        >
          Par taille
        </FilterButton>
      </FilterContainer>

      <ChartTitle>{getChartTitle()}</ChartTitle>

      {/* Conteneur pour les boutons d'export et de changement de type de graphique */}
      <ActionsContainer>
        <ExportContainer>
          <ExportButton onClick={exportToPNG}>
            <span>📊</span> Exporter PNG
          </ExportButton>
          <ExportButton onClick={exportToPDF}>
            <span>📄</span> Exporter PDF
          </ExportButton>
        </ExportContainer>

        {/* Boutons pour changer le type de graphique */}
        <ChartTypeContainer>
          <ChartTypeButton 
            selected={chartType === "bar"} 
            onClick={() => setChartType("bar")}
          >
            <span>📊</span> Barres
          </ChartTypeButton>
          <ChartTypeButton 
            selected={chartType === "line"} 
            onClick={() => setChartType("line")}
          >
            <span>📈</span> Ligne
          </ChartTypeButton>
          <ChartTypeButton 
            selected={chartType === "area"} 
            onClick={() => setChartType("area")}
          >
            <span>🌊</span> Aire
          </ChartTypeButton>
          <ChartTypeButton 
            selected={chartType === "pie"} 
            onClick={() => setChartType("pie")}
          >
            <span>🥧</span> Camembert
          </ChartTypeButton>
        </ChartTypeContainer>
      </ActionsContainer>

      <ChartContainer ref={chartRef}>
        {renderChart()}
      </ChartContainer>

      <DataTable data={data} selectedFilter={selectedFilter} />
    </VisualizerContainer>
  );
}

export default DataVisualizer;
